import { useEffect, useRef } from 'react';
import {
  Archive,
  Calendar,
  Check,
  CheckSquare,
  Copy,
  Eye,
  Flame,
  FolderInput,
  Forward,
  Loader2,
  Mail,
  MailOpen,
  MoreHorizontal,
  Package,
  RefreshCcw,
  Reply,
  ReplyAll,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Tag,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { ReplyMode } from './MessageMetadata';
import { useUIStore } from '@/stores/uiStore';
import { useOptionalEmailTaskCreation } from './EmailTaskCreation';
import { useOptionalEmailEventCreation } from './EmailEventCreation';
import { useOptionalEmailProductLineCreation } from './EmailProductLineCreation';

interface MobileActionBarProps {
  emailId: string;
  isRead?: boolean;
  onArchive: (emailId: string) => void;
  onDelete: (emailId: string) => void;
  /** Omit for surfaces where replying makes no sense (e.g. sender groups). */
  onReply?: (mode: ReplyMode) => void;
  archiveDisabled?: boolean;
  deleteDisabled?: boolean;
  onMarkRead?: (emailId: string, read: boolean) => void;
  onApproveSender?: () => void;
  // Training props (optional)
  canTrainSpam?: boolean;
  canTrainGood?: boolean;
  isTrainingSpam?: boolean;
  isTrainingGood?: boolean;
  onTrainSpam?: () => void;
  onTrainGood?: () => void;
  onSummarize?: () => void;
  onRehydrate?: (emailId: string) => void;
  rehydrating?: boolean;
  onAddLabel?: () => void;
  onCopyAiPayload?: () => void;
  onViewOcrData?: () => void;
  onBlockSender?: () => void;
  onMoveToFolder?: () => void;
}

const MENU_ITEM_CLASS = 'gap-3 py-2.5';

/**
 * The one mobile action surface for reading email: four large buttons
 * (archive, delete, reply w/ submenu, overflow). While mounted it takes the
 * place of the bottom navigation (via uiStore.mobileReaderBarCount) so only
 * one bar occupies the bottom edge at a time.
 */
export function MobileActionBar({
  emailId,
  isRead,
  onArchive,
  onDelete,
  onReply,
  archiveDisabled,
  deleteDisabled,
  onMarkRead,
  onApproveSender,
  canTrainSpam,
  canTrainGood,
  isTrainingSpam,
  isTrainingGood,
  onTrainSpam,
  onTrainGood,
  onSummarize,
  onRehydrate,
  rehydrating,
  onAddLabel,
  onCopyAiPayload,
  onViewOcrData,
  onBlockSender,
  onMoveToFolder,
}: MobileActionBarProps) {
  const taskCreation = useOptionalEmailTaskCreation();
  const eventCreation = useOptionalEmailEventCreation();
  const productLineCreation = useOptionalEmailProductLineCreation();
  const adjustReaderBarCount = useUIStore((state) => state.adjustMobileReaderBarCount);
  // Anchor for creation popovers: menu items unmount when the menu closes, so
  // popovers anchored to them would lose their position. The More button stays.
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    adjustReaderBarCount(1);
    return () => adjustReaderBarCount(-1);
  }, [adjustReaderBarCount]);

  // After a menu item is selected, Radix closes the menu, returns focus to the
  // trigger, and the same tap's trailing pointer/click events reach the
  // document. A popover/dialog opened synchronously in the handler mounts in
  // the middle of that sequence and treats it as an outside interaction —
  // instant dismiss. Every item therefore runs deferred (next macrotask),
  // paired with onCloseAutoFocus preventDefault on the menu content.
  const runAfterMenuClose = (action: () => void) => {
    window.setTimeout(action, 0);
  };

  const hasCreationActions = Boolean(taskCreation || eventCreation || productLineCreation);
  const hasUtilityActions = Boolean(
    onSummarize || onAddLabel || onRehydrate || onCopyAiPayload || onViewOcrData || onApproveSender || onBlockSender,
  );
  const hasTrainingActions = Boolean(onTrainSpam || onTrainGood);
  const hasMailboxActions = Boolean(onMoveToFolder || onMarkRead);
  const hasOverflowActions = hasMailboxActions || hasCreationActions || hasUtilityActions || hasTrainingActions;

  return (
    <div className="pointer-events-none md:hidden fixed inset-x-0 bottom-0 z-40 flex justify-center">
      <div
        className="pointer-events-auto px-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
      >
        {/* Same plum dock surface as the bottom navigation, so the swap while
            reading looks like the bar changing state, not being replaced. */}
        <div className="surface-dark flex items-center gap-1 rounded-full px-2.5 py-1.5 shadow-2xl">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-12 w-12 rounded-full text-foreground/80 hover:text-foreground hover:bg-white/10"
            onClick={() => onArchive(emailId)}
            disabled={archiveDisabled}
            aria-label="Archive"
          >
            <Archive className="size-[22px]" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-12 w-12 rounded-full text-destructive/90 hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(emailId)}
            disabled={deleteDisabled}
            aria-label="Delete"
          >
            <Trash2 className="size-[22px]" />
          </Button>
          {onReply && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-full text-foreground/80 hover:text-foreground hover:bg-white/10"
                aria-label="Reply options"
              >
                <Reply className="size-[22px]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              side="top"
              className="mb-2 w-48"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <DropdownMenuItem
                className={MENU_ITEM_CLASS}
                onClick={() => runAfterMenuClose(() => onReply('reply'))}
              >
                <Reply className="h-4 w-4" />
                <span>Reply</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MENU_ITEM_CLASS}
                onClick={() => runAfterMenuClose(() => onReply('replyAll'))}
              >
                <ReplyAll className="h-4 w-4" />
                <span>Reply all</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={MENU_ITEM_CLASS}
                onClick={() => runAfterMenuClose(() => onReply('forward'))}
              >
                <Forward className="h-4 w-4" />
                <span>Forward</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {hasOverflowActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                ref={moreButtonRef}
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-full text-foreground/80 hover:text-foreground hover:bg-white/10"
                aria-label="More actions"
              >
                <MoreHorizontal className="size-[22px]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              className="mb-2 w-60"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              {onMoveToFolder && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onMoveToFolder)}
                >
                  <FolderInput className="h-4 w-4" />
                  <span>Move to folder</span>
                </DropdownMenuItem>
              )}
              {onMarkRead && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(() => onMarkRead(emailId, !isRead))}
                >
                  {isRead ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                  <span>{isRead ? 'Mark unread' : 'Mark read'}</span>
                </DropdownMenuItem>
              )}
              {hasMailboxActions && hasCreationActions && <DropdownMenuSeparator />}
              {taskCreation && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() =>
                    runAfterMenuClose(() => {
                      const anchor = moreButtonRef.current;
                      if (anchor) {
                        taskCreation.openPopover(anchor, { align: 'end', side: 'top' });
                      }
                    })
                  }
                >
                  <CheckSquare className="h-4 w-4" />
                  <span>Create task</span>
                </DropdownMenuItem>
              )}
              {eventCreation && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() =>
                    runAfterMenuClose(() => {
                      const anchor = moreButtonRef.current;
                      if (anchor) {
                        eventCreation.openPopover(anchor);
                      }
                    })
                  }
                >
                  <Calendar className="h-4 w-4" />
                  <span>Create event</span>
                </DropdownMenuItem>
              )}
              {productLineCreation && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() =>
                    runAfterMenuClose(() => {
                      const anchor = moreButtonRef.current;
                      if (anchor) {
                        productLineCreation.openPopover(anchor);
                      }
                    })
                  }
                >
                  <Package className="h-4 w-4" />
                  <span>Create product line</span>
                </DropdownMenuItem>
              )}
              {hasUtilityActions && (hasMailboxActions || hasCreationActions) && <DropdownMenuSeparator />}
              {onSummarize && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onSummarize)}
                >
                  <Sparkles className="h-4 w-4" />
                  <span>AI summary</span>
                </DropdownMenuItem>
              )}
              {onAddLabel && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onAddLabel)}
                >
                  <Tag className="h-4 w-4" />
                  <span>Add sender label</span>
                </DropdownMenuItem>
              )}
              {onRehydrate && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(() => onRehydrate(emailId))}
                  disabled={rehydrating}
                >
                  {rehydrating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="h-4 w-4" />
                  )}
                  <span>{rehydrating ? 'Re-fetching…' : 'Re-fetch'}</span>
                </DropdownMenuItem>
              )}
              {onCopyAiPayload && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() =>
                    runAfterMenuClose(() => {
                      void onCopyAiPayload();
                    })
                  }
                >
                  <Copy className="h-4 w-4" />
                  <span>Copy AI payload</span>
                </DropdownMenuItem>
              )}
              {onViewOcrData && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onViewOcrData)}
                >
                  <Eye className="h-4 w-4" />
                  <span>View OCR data</span>
                </DropdownMenuItem>
              )}
              {onApproveSender && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onApproveSender)}
                >
                  <Check className="h-4 w-4" />
                  <span>Approve sender</span>
                </DropdownMenuItem>
              )}
              {onBlockSender && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onBlockSender)}
                >
                  <ShieldOff className="h-4 w-4" />
                  <span>Block sender</span>
                </DropdownMenuItem>
              )}
              {hasTrainingActions && (hasMailboxActions || hasCreationActions || hasUtilityActions) && (
                <DropdownMenuSeparator />
              )}
              {onTrainSpam && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onTrainSpam)}
                  disabled={!canTrainSpam || isTrainingSpam}
                >
                  {isTrainingSpam ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Flame className="h-4 w-4" />
                  )}
                  <span>{isTrainingSpam ? 'Training…' : canTrainSpam ? 'Train spam' : 'Spam trained'}</span>
                </DropdownMenuItem>
              )}
              {onTrainGood && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onClick={() => runAfterMenuClose(onTrainGood)}
                  disabled={!canTrainGood || isTrainingGood}
                >
                  {isTrainingGood ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  <span>{isTrainingGood ? 'Training…' : canTrainGood ? 'Train good' : 'Good trained'}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
